/**
 * Shared URL safety helpers for QDS components.
 *
 * Components frequently take author-supplied href/src values and interpolate
 * them into markup. HTML-escaping alone does not neutralize dangerous URL
 * schemes (e.g. `javascript:`), so use these helpers before rendering or
 * persisting any URL that originates from user/host input.
 */

const SAFE_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:', 'ftp:']);

/**
 * Returns the URL if it uses a safe scheme (or is a scheme-relative, path,
 * fragment, or query reference), otherwise returns an empty string.
 *
 * Relative URLs (no scheme) are always allowed. Absolute URLs are only allowed
 * when their scheme is in the allowlist, which blocks `javascript:`, `data:`,
 * `vbscript:`, `blob:`, and similar executable/opaque schemes.
 */
export function sanitizeUrl(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  // Relative references (path, query, fragment, protocol-relative) have no
  // scheme and are safe to pass through.
  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('?') ||
    trimmed.startsWith('./') ||
    trimmed.startsWith('../')
  ) {
    return trimmed;
  }

  // A leading scheme is `scheme:` where scheme starts with a letter and
  // contains only letters, digits, `+`, `-`, or `.`. If there is no such
  // prefix, treat the value as a relative reference.
  const schemeMatch = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(trimmed);
  if (!schemeMatch) {
    return trimmed;
  }

  const scheme = schemeMatch[1].toLowerCase() + ':';
  return SAFE_URL_SCHEMES.has(scheme) ? trimmed : '';
}
